import type { LayerKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import type { ChangeOperation } from '../../uiModels/workspace/types'
import type {
  DraftIdentifier,
  DraftSubmitStatus,
} from '../draftCreationModal/types'
import { OperationFileUploadForm } from '../operationFileUploadForm/OperationFileUploadForm'

const EXCEL_FILE_EXTENSION = '.xlsx'

export type ExcelUploadConfig<TOperation extends ChangeOperation> = {
  convertToOperation: (excelFile: File) => Promise<Optional<TOperation>>
  getEntityId: (operation: TOperation) => DraftIdentifier
  missingEntityNameError: string
  getEntityNameMismatchError: (
    entityName: string,
    expectedEntityName: string,
  ) => string
  getUploadErrorMessage: (error: string) => string
}

type Props<TOperation extends ChangeOperation> = {
  formId: string
  layer: LayerKey
  block: string
  excelConfig: ExcelUploadConfig<TOperation>
  expectedEntityName?: string
  onStatusChanged: (status: DraftSubmitStatus) => void
}

export const ExcelUploadForm = <TOperation extends ChangeOperation>({
  excelConfig: {
    convertToOperation,
    getUploadErrorMessage,
    getEntityId,
    missingEntityNameError,
    getEntityNameMismatchError,
  },
  ...uploadProps
}: Props<TOperation>) => {
  const translate = useTranslate()

  return (
    <OperationFileUploadForm
      {...uploadProps}
      fileConfig={{
        fileExtension: EXCEL_FILE_EXTENSION,
        label: translate('documentFormat.excel'),
        description: translate('excelUploadForm.file.description'),
        invalidFileTypeError: translate('excelUploadForm.file.invalidType'),
        convertToOperation,
      }}
      entityConfig={{
        getEntityId,
        missingEntityNameError,
        getEntityNameMismatchError,
      }}
      getUploadErrorMessage={getUploadErrorMessage}
    />
  )
}
